/* ArtifactFrame: the sandboxed iframe for artifacts and previews, with the parent side of the runtime
   bridge (api/src/modules/build/README.md section 2).
   - Origin: never `allow-same-origin`, so the page runs on an opaque origin ("null") and cannot read
     Crosspoint cookies or call the API. The render URL already carries the artifact CSP header.
   - Messages: the page posts `{type:'cx:request', v:1, id, method, params}` to its parent; this
     component accepts it only when `event.source === iframe.contentWindow`, calls the runtime route
     as the viewer (cookie session), and answers `{type:'cx:response', v:1, id, result | error}`.
     `{type:'cx:resize', v:1, height}` sizes the frame when `autoHeight` is on.
   - Permissions: a 403 `permission_required` shows a prompt naming the capability, then
     POST /grants {capability, allow} and a retry; `capability_not_declared` is never prompted.
     Every `cx.download` shows a save prompt before the file is saved.
   Chat reuses this for artifact panes (docs/core-requests.md). */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Download, ShieldQuestion } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { Button, Dialog } from '../../ui';
import { cx } from '../../ui/cx';
import { formatBytes, grantFor, grantPrompt, isBridgeRequest, runtimeRoute, type RuntimeCall, type RuntimeMethod } from './logic';

export const IFRAME_SANDBOX = 'allow-scripts allow-forms allow-popups allow-modals allow-downloads';
export const IFRAME_ALLOW = "clipboard-write; camera 'none'; microphone 'none'; geolocation 'none'";

/** How the frame reaches the API. The default calls the build runtime routes with the cookie session. */
export interface ArtifactBridge {
  call: (c: RuntimeCall) => Promise<unknown>;
  grant: (artifactId: string, capability: string, allow: boolean) => Promise<unknown>;
  /** Saves a file the bridge produced (download). */
  save: (url: string, name: string) => void;
}

const saveLink = (url: string, name: string) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const httpBridge: ArtifactBridge = {
  call: (c) => (c.method === 'GET' ? api.get(c.path, { query: c.query }) : api.post(c.path, c.body)),
  grant: (artifactId, capability, allow) => api.post(`/api/v1/build/runtime/${encodeURIComponent(artifactId)}/grants`, { capability, allow }),
  save: saveLink,
};

type PromptSpec = { kind: 'grant'; grant: string } | { kind: 'save'; name: string; size: number };
type Prompt = PromptSpec & { resolve: (ok: boolean) => void };

export interface ArtifactFrameProps {
  /** Render URL from `preview-url` / `render-url`. */
  src: string | null | undefined;
  title: string;
  /** Artifact id for the runtime bridge; without it every bridge call answers capability_not_declared. */
  artifactId?: string;
  bridge?: ArtifactBridge;
  /** Grow to the height the page reports (cx:resize), clamped to [minHeight, maxHeight]. */
  autoHeight?: boolean;
  minHeight?: number;
  maxHeight?: number;
  className?: string;
  /** Reported after each bridge call (tests, activity logs). */
  onBridgeCall?: (method: RuntimeMethod, ok: boolean) => void;
  /** Override the frame window lookup (tests). */
  frameWindow?: () => Window | null;
}

export function ArtifactFrame({
  src, title, artifactId, bridge = httpBridge, autoHeight, minHeight = 240, maxHeight = 2000, className, onBridgeCall, frameWindow,
}: ArtifactFrameProps) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<number | null>(null);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const bridgeRef = useRef(bridge);
  bridgeRef.current = bridge;
  const cbRef = useRef(onBridgeCall);
  cbRef.current = onBridgeCall;

  const ask = useCallback((p: PromptSpec) => new Promise<boolean>((resolve) => {
    setPrompts((l) => [...l, { ...p, resolve }]);
  }), []);
  const settle = (ok: boolean) => setPrompts((l) => { l[0]?.resolve(ok); return l.slice(1); });

  useEffect(() => {
    const win = () => (frameWindow ? frameWindow() : ref.current?.contentWindow ?? null);
    const reply = (id: string, body: { result?: unknown; error?: { code: string; message: string } }) => {
      win()?.postMessage({ type: 'cx:response', v: 1, id, ...body }, '*');
    };

    const handle = async (id: string, method: RuntimeMethod, params: Record<string, unknown>) => {
      if (!artifactId) { reply(id, { error: { code: 'capability_not_declared', message: 'This preview has no runtime capabilities.' } }); return; }
      const b = bridgeRef.current;
      const route = runtimeRoute(artifactId, method, params);
      let prompted = false;
      for (;;) {
        try {
          let result = await b.call(route);
          if (method === 'download') {
            const r = result as { blobId?: string; url?: string; name?: string; size?: number };
            const name = r.name ?? String(params.name ?? 'download');
            const ok = await ask({ kind: 'save', name, size: r.size ?? String(params.content ?? '').length });
            if (!ok) { reply(id, { error: { code: 'download_cancelled', message: 'The viewer did not save the file.' } }); cbRef.current?.(method, false); return; }
            const inline = r.url && /^(data|blob):/.test(r.url);
            b.save(inline || !r.blobId ? r.url ?? '' : `/api/v1/build/downloads/${encodeURIComponent(r.blobId)}`, name);
            result = { name, size: r.size, saved: true };
          }
          reply(id, { result });
          cbRef.current?.(method, true);
          return;
        } catch (err) {
          const code = err instanceof ApiError ? err.code : 'error';
          const message = err instanceof Error ? err.message : 'Request failed';
          const grant = grantFor(method, params);
          if (code === 'permission_required' && grant && !prompted) {
            prompted = true;
            const allow = await ask({ kind: 'grant', grant });
            try { await b.grant(artifactId, grant, allow); } catch { /* the retry reports the real state */ }
            if (allow) continue;
            reply(id, { error: { code: 'permission_denied', message: `You did not allow "${grant}".` } });
            cbRef.current?.(method, false);
            return;
          }
          reply(id, { error: { code, message } });
          cbRef.current?.(method, false);
          return;
        }
      }
    };

    const onMessage = (e: MessageEvent) => {
      const w = win();
      if (!w || e.source !== w) return; // only our own frame (its origin is the opaque "null")
      const d = e.data as { type?: string; v?: number; height?: unknown };
      if (d?.type === 'cx:resize' && d.v === 1) {
        const h = Number(d.height);
        if (Number.isFinite(h)) setHeight(Math.max(minHeight, Math.min(maxHeight, Math.round(h))));
        return;
      }
      if (isBridgeRequest(d)) void handle(d.id, d.method, (d.params ?? {}) as Record<string, unknown>);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [artifactId, ask, frameWindow, minHeight, maxHeight]);

  const p = prompts[0];
  const gp = p?.kind === 'grant' ? grantPrompt(p.grant, title) : null;
  return (
    <div className={cx('bx-frame', className)} data-cap="BUILD-01">
      {src ? (
        <iframe
          ref={ref}
          src={src}
          title={title}
          sandbox={IFRAME_SANDBOX}
          allow={IFRAME_ALLOW}
          referrerPolicy="no-referrer"
          style={autoHeight && height ? { height } : undefined}
        />
      ) : <div className="bx-frame-empty small muted">No preview yet.</div>}
      <Dialog
        open={Boolean(p)}
        onClose={() => settle(false)}
        title={gp ? gp.title : p?.kind === 'save' ? 'Save this file?' : ''}
        footer={p && (
          <>
            <Button onClick={() => settle(false)}>{p.kind === 'grant' ? "Don't allow" : 'Cancel'}</Button>
            <Button variant="primary" onClick={() => settle(true)}>{p.kind === 'grant' ? 'Allow' : 'Save'}</Button>
          </>
        )}
      >
        {p?.kind === 'grant' && gp && (
          <div className="bx-prompt"><ShieldQuestion size={20} aria-hidden="true" /><p>{gp.body} You can change this later in the artifact's permissions.</p></div>
        )}
        {p?.kind === 'save' && (
          <div className="bx-prompt"><Download size={20} aria-hidden="true" /><p>"{title}" wants to save <b>{p.name}</b> ({formatBytes(p.size)}) to your device.</p></div>
        )}
      </Dialog>
    </div>
  );
}

export default ArtifactFrame;
